import React, { useState, useRef } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import { uploadPromotionImage } from '../../../services/storageService';
import { addPromotion, updatePromotion, deletePromotion } from '../../../services/promotionsService';

const PromotionsManager = ({ promotions = [], onRefresh }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ title: '', description: '', imageUrl: '', active: true });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const imageInputRef = useRef(null);

  const resetForm = () => {
    setFormData({ title: '', description: '', imageUrl: '', active: true });
    setImageFile(null);
    setImagePreview('');
    setError('');
    setIsAdding(false);
    setEditingId(null);
  };

  const handleImageChange = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setError('');
  };

  const handleStartEdit = (promo) => {
    setEditingId(promo?.id);
    setFormData({ title: promo?.title, description: promo?.description, imageUrl: promo?.imageUrl, active: promo?.active });
    setImagePreview(promo?.imageUrl || '');
    setImageFile(null);
    setIsAdding(false);
    setError('');
  };

  const handleSave = async () => {
    if (!imageFile && !formData?.imageUrl) { setError("Une image est requise."); return; }

    setIsUploading(true);
    setError('');
    try {
      let imageUrl = formData?.imageUrl;
      if (imageFile) {
        imageUrl = await uploadPromotionImage(imageFile);
      }
      const payload = { ...formData, imageUrl };
      if (editingId) {
        await updatePromotion(editingId, payload);
      } else {
        await addPromotion(payload);
      }
      resetForm();
      onRefresh?.();
    } catch (err) {
      setError('Échec de la sauvegarde. Veuillez réessayer.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette promotion ?')) return;
    try {
      await deletePromotion(id);
      onRefresh?.();
    } catch (err) {
      setError('Échec de la suppression.');
    }
  };

  return (
    <div className="promotions-manager bg-card border border-border rounded-lg shadow-warm p-4 md:p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Icon name="Megaphone" size={20} color="var(--color-primary)" />
          <h2 className="text-base md:text-lg font-heading font-semibold text-foreground">
            Promotions
          </h2>
          <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium">
            {promotions?.length}
          </span>
        </div>
        {!isAdding && !editingId && (
          <Button
            variant="default"
            onClick={() => { setIsAdding(true); setEditingId(null); setFormData({ title: '', description: '', imageUrl: '', active: true }); setImagePreview(''); setImageFile(null); setError(''); }}
            iconName="Plus"
            iconPosition="left"
            iconSize={16}
          >
            Ajouter
          </Button>
        )}
      </div>

      {/* Add / Edit Form */}
      {(isAdding || editingId) && (
        <div className="bg-muted/30 border border-border rounded-lg p-4 mb-4 space-y-3">
          <h3 className="text-sm font-semibold text-foreground">
            {editingId ? 'Modifier la promotion' : 'Nouvelle promotion'}
          </h3>

          <Input
            type="text"
            label="Titre"
            placeholder="Ex: -20% sur les desserts"
            value={formData?.title}
            onChange={(e) => setFormData(prev => ({ ...prev, title: e?.target?.value }))}
          />

          <Input
            type="text"
            label="Description (optionnel)"
            placeholder="Détails de la promotion"
            value={formData?.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e?.target?.value }))}
          />

          {/* Image Upload */}
          <div className="space-y-1">
            <label className="block text-sm font-medium text-foreground">Image de la promotion</label>
            <div
              className="border-2 border-dashed border-border rounded-lg p-3 flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-primary/50 hover:bg-muted/30 transition-colors min-h-[80px]"
              onClick={() => imageInputRef?.current?.click()}
            >
              {imagePreview ? (
                <img src={imagePreview} alt="Aperçu promotion" className="h-24 w-full object-cover rounded-md" />
              ) : (
                <>
                  <Icon name="ImagePlus" size={20} color="var(--color-muted-foreground)" />
                  <p className="text-xs text-muted-foreground">Cliquez pour choisir une image</p>
                </>
              )}
              {imageFile && <p className="text-xs text-primary font-medium truncate max-w-full">{imageFile?.name}</p>}
            </div>
            <input
              ref={imageInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              className="hidden"
              onChange={handleImageChange}
            />
          </div>

          {/* Active toggle */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData?.active}
              onChange={(e) => setFormData(prev => ({ ...prev, active: e?.target?.checked }))}
              className="w-4 h-4 rounded border-border text-primary"
            />
            <span className="text-sm text-foreground">Promotion active (visible aux clients)</span>
          </label>

          {error && <p className="text-xs text-red-500">{error}</p>}

          <div className="flex gap-2 pt-1">
            <Button variant="default" onClick={handleSave} disabled={isUploading} className="flex-1">
              {isUploading ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
            <Button variant="outline" onClick={resetForm} disabled={isUploading} className="flex-1">
              Annuler
            </Button>
          </div>
        </div>
      )}

      {/* Promotions List */}
      {promotions?.length === 0 && !isAdding ? (
        <div className="text-center py-8 text-muted-foreground">
          <Icon name="Megaphone" size={32} color="var(--color-muted-foreground)" />
          <p className="text-sm mt-2">Aucune promotion pour le moment.</p>
          <p className="text-xs mt-1">Ajoutez une promotion pour qu'elle apparaisse aux clients.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {promotions?.map((promo) => (
            <div
              key={promo?.id}
              className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${promo?.active ? 'border-border bg-background' : 'border-border/50 bg-muted/20 opacity-60'}`}
            >
              {promo?.imageUrl ? (
                <img
                  src={promo?.imageUrl}
                  alt={promo?.title || 'Promotion'}
                  className="w-16 h-12 object-cover rounded-md flex-shrink-0"
                />
              ) : (
                <div className="w-16 h-12 bg-muted rounded-md flex items-center justify-center flex-shrink-0">
                  <Icon name="Image" size={16} color="var(--color-muted-foreground)" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{promo?.title}</p>
                {promo?.description && (
                  <p className="text-xs text-muted-foreground truncate">{promo?.description}</p>
                )}
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${promo?.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                  {promo?.active ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={() => handleStartEdit(promo)}
                  className="p-1.5 rounded-md hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                  title="Modifier"
                >
                  <Icon name="Pencil" size={14} />
                </button>
                <button
                  onClick={() => handleDelete(promo?.id)}
                  className="p-1.5 rounded-md hover:bg-red-50 transition-colors text-muted-foreground hover:text-red-500"
                  title="Supprimer"
                >
                  <Icon name="Trash2" size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PromotionsManager;
